import React, { useState } from "react";

import { useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Typography, IconButton } from "@mui/material";
import { isEmpty } from "lodash";
import { makeStyles, withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";

import EditModal from "./EditModal";
import CertificationIcon from "../../../components/Icons/CertificationIcon";
import DumbellIcon from "../../../components/Icons/DumbellIcon";
import EducationIcon from "../../../components/Icons/EducationIcon";
import NoCertificationIcon from "../../../components/Icons/NoCertificationIcon";
import NoEducationIcon from "../../../components/Icons/NoEducationIcon";
import NoTrainingsIcon from "../../../components/Icons/NoTrainingsIcon";

const useStyles = makeStyles()((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    padding: theme.spacing(2, 3, 4, 3),
    backgroundColor: colors.white,
    marginBottom: theme.spacing(2),
  },
  headerText: {
    fontSize: "18px",
    fontWeight: "bold",
  },

  content: {
    display: "flex",
    flexDirection: "column",
    width: "80%",
    margin: theme.spacing(4, 0),
  },
  sectionHeader: {
    display: "flex",
    flexDirection: "row",
  },
  sectionHeaderText: {
    fontSize: "16px",
    fontWeight: 700,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "24px",
  },
  sectionContent: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  infoCard: {
    display: "flex",
    flexDirection: "row",
    backgroundColor: colors.white,
    padding: theme.spacing(3),
    marginRight: theme.spacing(5),
    marginTop: theme.spacing(2),
  },
  info: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginLeft: "14px",
  },
  iconContainer: {
    backgroundColor: "#e9fbf7",
    borderRadius: "48px",
    padding: theme.spacing(1, 2),
  },
  loadingView: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  emptyView: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "18px",
    marginLeft: "96px",
  },
  emptyText: {
    fontSize: "14px",
    color: "#8D96A4",
  },
  editIcon: {
    color: colors.primary,
    height: "18px",
    width: "18px",
  },
}));
const StyledEducationIcon = withStyles(EducationIcon, {
  root: {
    marginTop: "8px",
    height: "24px",
    width: "24px",
  },
});
const StyledDumbellIcon = withStyles(DumbellIcon, {
  root: {
    marginTop: "8px",
    height: "24px",
    width: "24px",
  },
});
const StyledCertificationIcon = withStyles(CertificationIcon, {
  root: {
    marginTop: "8px",
    height: "24px",
    width: "24px",
  },
});
const StyledNoEducationIcon = withStyles(NoEducationIcon, {
  root: {
    height: "44px",
    width: "44px",
  },
});
const StyledNoTrainingIcon = withStyles(NoTrainingsIcon, {
  root: {
    height: "44px",
    width: "44px",
  },
});
const StyledNoCertification = withStyles(NoCertificationIcon, {
  root: {
    height: "44px",
    width: "44px",
  },
});
const StyledIconButton = withStyles(IconButton, {
  root: {
    height: "12px",
    width: "12px",
    marginLeft: "4px",
  },
});

export const trainingsAndSpecializationQuery = graphql(`
  query TrainingsAndSpecialization {
    coachViewer {
      id
      account {
        id
        email
      }
      coachProfile {
        id
        trainings {
          id
          name
          year
        }
        educations {
          id
          name
          year
        }
        certifications {
          id
          name
          year
        }
      }
    }
  }
`);

const TrainingsAndSpecialization: React.FC = () => {
  const { classes: styles } = useStyles();
  const { data, loading } = useQuery(trainingsAndSpecializationQuery);
  const coachProfile = data?.coachViewer.coachProfile;
  const trainings = coachProfile?.trainings ?? [];
  const certifications = coachProfile?.certifications ?? [];
  const educations = coachProfile?.educations ?? [];
  const [modalVisibility, setModalVisibility] = useState<boolean>();
  const [modalData, setModalData] = useState<
    Array<{ id?: string; name: string; year: number }> | undefined
  >();
  const [modalType, setModalType] = useState<string>("");
  if (loading) {
    return (
      <div className={styles.loadingView}>
        <Typography variant="h6" color="textPrimary">
          Loading...
        </Typography>
      </div>
    );
  }

  function getEducations() {
    if (!isEmpty(educations)) {
      return (
        <>
          {educations?.map((item) => (
            <Box key={item.id} className={styles.infoCard}>
              <div className={styles.iconContainer}>
                <StyledEducationIcon />
              </div>
              <div className={styles.info}>
                <Typography variant="subtitle1" color="textPrimary">
                  {item?.name}
                </Typography>
              </div>
            </Box>
          ))}
        </>
      );
    } else {
      return (
        <div className={styles.emptyView}>
          <StyledNoEducationIcon />
          <Typography className={styles.emptyText}>
            No Education to show
          </Typography>
        </div>
      );
    }
  }

  function getTrainings() {
    if (!isEmpty(trainings)) {
      return (
        <>
          {trainings?.map((item) => (
            <Box key={item.id} className={styles.infoCard}>
              <div className={styles.iconContainer}>
                <StyledDumbellIcon />
              </div>
              <div className={styles.info}>
                <Typography variant="subtitle1" color="textPrimary">
                  {item?.name}
                </Typography>
              </div>
            </Box>
          ))}
        </>
      );
    } else {
      return (
        <div className={styles.emptyView}>
          <StyledNoTrainingIcon />
          <Typography className={styles.emptyText}>
            No Trainings to show
          </Typography>
        </div>
      );
    }
  }

  function getCertifications() {
    if (!isEmpty(certifications)) {
      return (
        <>
          {certifications?.map((item) => (
            <Box key={item.id} className={styles.infoCard}>
              <div className={styles.iconContainer}>
                <StyledCertificationIcon />
              </div>
              <div className={styles.info}>
                <Typography variant="subtitle1" color="textPrimary">
                  {item?.name}
                </Typography>
              </div>
            </Box>
          ))}
        </>
      );
    } else {
      return (
        <div className={styles.emptyView}>
          <StyledNoCertification />
          <Typography className={styles.emptyText}>
            No Certifications to show
          </Typography>
        </div>
      );
    }
  }

  const handleOpen = () => {
    setModalVisibility(true);
  };

  const handleClose = () => {
    setModalVisibility(false);
  };
  return (
    <div className={styles.root}>
      <Box className={styles.header}>
        <Typography variant="h6" color="textPrimary">
          Trainings and Specialization
        </Typography>
      </Box>
      <Box className={styles.content}>
        <Box className={styles.section}>
          <div className={styles.sectionHeader}>
            <Typography className={styles.sectionHeaderText}>
              Education details
            </Typography>
            <StyledIconButton
              aria-label="edit"
              onClick={() => {
                setModalData(educations);
                setModalType("education");
                handleOpen();
              }}
            >
              <EditIcon className={styles.editIcon} />
            </StyledIconButton>
          </div>
          <Box className={styles.sectionContent}>{getEducations()}</Box>
        </Box>

        <Box className={styles.section}>
          <div className={styles.sectionHeader}>
            <Typography className={styles.sectionHeaderText}>
              Training details
            </Typography>
            <StyledIconButton
              aria-label="edit"
              onClick={() => {
                setModalData(trainings);
                setModalType("trainings");
                handleOpen();
              }}
            >
              <EditIcon className={styles.editIcon} />
            </StyledIconButton>
          </div>
          <Box className={styles.sectionContent}>{getTrainings()}</Box>
        </Box>

        <Box className={styles.section}>
          <div className={styles.sectionHeader}>
            <Typography className={styles.sectionHeaderText}>
              Certification details
            </Typography>
            <StyledIconButton
              aria-label="edit"
              onClick={() => {
                setModalData(certifications);
                setModalType("certification");
                handleOpen();
              }}
            >
              <EditIcon className={styles.editIcon} />
            </StyledIconButton>
          </div>
          <Box className={styles.sectionContent}>{getCertifications()}</Box>
        </Box>
      </Box>

      <EditModal
        modalType={modalType}
        data={modalData}
        onOpen={modalVisibility}
        onClose={handleClose}
      />
    </div>
  );
};

export default TrainingsAndSpecialization;
